import React, { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { useSite } from "@/context/SiteContext";
import Seo from "@/components/Seo";
import PublicLayout from "@/components/PublicLayout";
import VehicleCard from "@/components/VehicleCard";
import { Breadcrumbs } from "@/components/Marketing";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SlidersHorizontal, Loader2 } from "lucide-react";

const FILTERS = [
  { key: "availability", label: "Availability", options: [["", "All"], ["available", "Available Now"], ["coming_soon", "Coming Soon"]] },
  { key: "phv_status", label: "PHV Status", options: [["", "All"], ["Manchester PHV Licensed", "Manchester PHV Licensed"], ["Manchester PHV Licence Pending", "Licence Pending"], ["Suitable for PHV Licensing", "Suitable for Licensing"]] },
  { key: "fuel", label: "Fuel", options: [["", "All"], ["Hybrid", "Hybrid"], ["Petrol", "Petrol"], ["Electric", "Electric"], ["Diesel", "Diesel"]] },
  { key: "transmission", label: "Transmission", options: [["", "All"], ["Automatic", "Automatic"], ["Manual", "Manual"]] },
  { key: "insurance", label: "Insurance", options: [["", "All"], ["with", "With Insurance"], ["without", "Without Insurance"]] },
  { key: "sort", label: "Sort", options: [["recently_added", "Recently Added"], ["price_asc", "Price Low to High"], ["price_desc", "Price High to Low"], ["newest_car", "Newest Car"]] },
];

export default function Cars() {
  const { stats } = useSite();
  const [filters, setFilters] = useState({ sort: "recently_added" });
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/vehicles", { params }).then((r) => setVehicles(r.data)).finally(() => setLoading(false));
  }, [filters]);

  const availableNow = vehicles.filter((v) => v.availability === "available").length;

  return (
    <PublicLayout>
      <Seo
        title="Manchester Licensed PHV Cars for Rent"
        description="Browse Manchester licensed PHV cars available for weekly rental. Filter by availability, PHV status, fuel, transmission and insurance. WhatsApp us to rent."
        path="/cars"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Cars" }]} />

      <div className="container-x">
        <h1 className="font-heading text-4xl font-black uppercase tracking-tighter sm:text-5xl">
          Manchester Licensed PHV Cars for Rent
        </h1>
        <p className="mt-3 inline-flex items-center gap-2 text-lg font-semibold text-zinc-700" data-testid="cars-available-count">
          <span className="live-dot" /> {stats.available_count} Vehicles Available Now
        </p>
      </div>

      {/* Filters */}
      <div className="container-x mt-8">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-zinc-500">
          <SlidersHorizontal className="h-4 w-4" /> Filter
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {FILTERS.map((f) => (
            <div key={f.key}>
              <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-zinc-500">{f.label}</label>
              <Select
                value={filters[f.key] || (f.key === "sort" ? "recently_added" : "")}
                onValueChange={(val) => setFilters((prev) => ({ ...prev, [f.key]: val === "__all" ? "" : val }))}
              >
                <SelectTrigger data-testid={`filter-${f.key}`} className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {f.options.map(([val, lbl]) => (
                    <SelectItem key={val || "all"} value={val === "" ? "__all" : val}>{lbl}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>

      <div className="container-x py-10">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-zinc-400" /></div>
        ) : vehicles.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center text-zinc-500" data-testid="cars-empty">
            No vehicles match these filters. Try clearing filters or WhatsApp us for current availability.
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {vehicles.map((v, i) => <VehicleCard key={v.id} v={v} index={i} />)}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
